import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

const THERAPIST_1 = "https://images.unsplash.com/photo-1592621385612-4d7129426394?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxlbXBhdGhldGljJTIwdGhlcmFwaXN0JTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc4ODUxNzM0fDA&ixlib=rb-4.1.0&q=85";
const THERAPIST_2 = "https://images.unsplash.com/photo-1592621385645-e41659e8aabe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxlbXBhdGhldGljJTIwdGhlcmFwaXN0JTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc4ODUxNzM0fDA&ixlib=rb-4.1.0&q=85";
const SOPHIA = "/team/sophia-dinwiddie-donald.png";
const ASHAKI = "/team/ashaki-gilmer.jpg";
const SHAREE = "/team/sharee-lewis.jpg";

const team = [
  {
    name: "Sophia Dinwiddie-Donald",
    credentials: "MSSW, LCSW-S",
    role: "Founder & Clinical Leader",
    image: SOPHIA,
    fallback: THERAPIST_1,
    bio: "Sophia Dinwiddie-Donald, MSSW, LCSW-S is the founder and clinical leader of Seven Pillars of Life. She provides compassionate therapy, clinical supervision, coaching, and program leadership with a focus on life transitions, emotional healing, relationships, family dynamics, and personal growth. Sophia is dedicated to creating a supportive, culturally responsive space where clients and clinicians feel seen, strengthened, and empowered to move forward with confidence.",
  },
  {
    name: "Ashaki Gilmer",
    credentials: "",
    role: "Clinical Director",
    image: ASHAKI,
    fallback: THERAPIST_2,
    bio: "Ashaki Gilmer serves as Clinical Director at Seven Pillars of Life, providing leadership, clinical support, and program guidance for our team. With a strong commitment to compassionate care, client advocacy, and professional development, she helps support quality services, staff collaboration, and ethical practice. Ashaki brings warmth, structure, and dedication to helping clients and clinicians grow with confidence and purpose.",
  },
  {
    name: "Sha-ree D. Lewis",
    credentials: "MBA, MSc, CHPC",
    role: "Transformation Strategist",
    image: SHAREE,
    fallback: THERAPIST_2,
    bio: "Sha-ree D. Lewis, MBA, MSc, CHPC, is a Transformation Strategist, Organizational Leadership Consultant, and founder of Measureless Growth. Creator of the FVSSE Framework™, she helps faith-driven leaders strengthen alignment, build structure, improve execution, and achieve sustainable, measurable growth.",
    website: { url: "http://measurelessgrowth.com", label: "measurelessgrowth.com" },
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Sophia Dinwiddie-Donald, LCSW-S & Team"
        description="Meet the Seven Pillars of Life clinical team — therapists and supervisors helping Texans navigate anxiety, ADHD, autism, relationships, and clinical licensure."
        path="/about"
      />
      <PageHeader
        overline="About Us"
        title={<>Therapy rooted in <em className="italic text-terracotta">presence</em>, practiced with intention.</>}
        subtitle="Seven Pillars of Life Therapy is a Texas-based practice dedicated to helping adults navigate change, rebuild stability, and move forward with confidence — one pillar at a time."
        image={SOPHIA}
      />

      {/* OUR STORY */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="overline mb-5">Our Story</p>
          <h2 className="font-display text-3xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
            Built on the belief that healing is <em className="italic text-terracotta">structured, supported, and personal.</em>
          </h2>
          <div className="mt-8 space-y-5 text-lg text-ink/80 leading-relaxed">
            <p>
              At Seven Pillars of Life, we are dedicated to empowering individuals through compassionate counseling and coaching, fostering growth and resilience in every stage of life.
            </p>
            <p>
              Our practice was founded on a simple idea: lasting change happens when you feel truly seen, supported by a trained clinician, and given practical tools that translate insight into action. We specialize in cognitive behavioral therapy, life skills coaching for ADHD and autism, betrayal trauma therapy, and LCSW supervision across Texas.
            </p>
            <p>
              We also believe in growing the field of mental health. Through our internship and LMSW supervision programs, we support emerging clinicians as they build the skills, confidence, and clinical wisdom to serve their own communities.
            </p>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-20 md:py-28 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-5">Meet the Team</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              The hands and hearts <em className="italic text-terracotta">behind the work.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {team.map((m) => (
              <article
                key={m.name}
                data-testid={`team-card-${m.name.split(" ")[0].toLowerCase()}`}
                className="bg-surface border border-sage/25 rounded-2xl overflow-hidden hover:border-terracotta/40 hover:shadow-xl transition-all duration-300 grid grid-cols-1 sm:grid-cols-5"
              >
                <div className="sm:col-span-2 aspect-[4/5] sm:aspect-auto overflow-hidden bg-sage/20 relative">
                  <img
                    src={m.image}
                    alt={m.name}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const sib = e.currentTarget.nextElementSibling;
                      if (sib) sib.style.display = "flex";
                    }}
                    className="w-full h-full object-cover"
                  />
                  <div className="hidden absolute inset-0 w-full h-full items-center justify-center bg-surface text-forest/30">
                    <span className="font-display font-medium text-6xl">{m.name.split(" ").map(n => n[0]).join("").slice(0, 2)}</span>
                  </div>
                </div>
                <div className="sm:col-span-3 p-7 md:p-9 flex flex-col justify-center">
                  <p className="overline mb-3">{m.role}</p>
                  <h3 className="font-display text-2xl md:text-3xl text-forest font-medium leading-tight">{m.name}</h3>
                  {m.credentials && <p className="text-ink/60 mt-1 text-sm">{m.credentials}</p>}
                  <p className="mt-4 text-ink/75 leading-relaxed">{m.bio}</p>
                  {m.website && (
                    <a
                      href={m.website.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`team-website-${m.name.split(" ")[0].toLowerCase()}`}
                      className="mt-4 inline-flex items-center gap-1 text-terracotta font-medium link-underline text-sm"
                    >
                      Visit {m.website.label} →
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-forest text-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight">
            Curious whether we're <em className="italic text-terracotta">the right fit?</em>
          </h2>
          <p className="mt-5 text-bone/80 max-w-2xl mx-auto">
            Schedule a free 15-minute consultation. We'll listen, answer your questions, and help you decide your next step.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="mt-8 inline-flex items-center bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-8 py-4 font-medium transition-colors"
          >
            Get in touch
          </Link>
        </div>
      </section>
    </div>
  );
}
